(() => {
  'use strict';
  if (!customElements.get('header-account-photo')) {
    customElements.define('header-account-photo', class extends HTMLElement {
      connectedCallback() {
        this.cancelImage();
        if (this.querySelector('.header-account__image')) return;
        const avatar = this.querySelector('[data-header-account-avatar]')?.content.querySelector('shop-user-avatar[src]');
        const source = avatar?.getAttribute('src')?.trim();
        if (!source) return;
        let url;
        try {
          url = new URL(source, document.baseURI);
          if (url.protocol !== 'https:') return;
        } catch { return; }
        const image = new Image(36, 36);
        this.pendingImage = image;
        this.imageEvents = new AbortController();
        const signal = this.imageEvents.signal;
        image.className = 'header-account__image';
        image.alt = '';
        image.decoding = 'async';
        image.addEventListener('load', () => {
          if (!this.isConnected || this.pendingImage !== image || !image.naturalWidth) return;
          this.replaceChildren(image);
          this.classList.add('is-photo-ready');
          this.cancelImage();
        }, { once: true, signal });
        image.addEventListener('error', () => {
          if (this.pendingImage === image) this.cancelImage();
        }, { once: true, signal });
        image.src = url.href;
      }
      cancelImage() {
        this.imageEvents?.abort();
        this.imageEvents = null;
        this.pendingImage = null;
      }
      disconnectedCallback() { this.cancelImage(); }
    });
  }
  if (!window.OleaHeaderPanels) {
    let active = null;
    let pending = null;
    let intent = 0;
    const headers = () => [...document.querySelectorAll('smart-header')].filter(header => header.isConnected && header.section?.isConnected && header.offsetHeight > 0);
    const settled = header => {
      const section = header.section;
      if (!section?.isConnected || section.classList.contains('is-header-hidden')) return false;
      const transform = getComputedStyle(section).transform;
      if (transform !== 'none' && Math.abs(new DOMMatrixReadOnly(transform).m42) > .1) return false;
      return !(section.getAnimations?.() || []).some(animation => animation.transitionProperty === 'transform' && (animation.playState === 'running' || animation.pending));
    };
    const cancel = source => {
      if (!pending || source && pending.source !== source) return;
      const request = pending;
      pending = null;
      intent += 1;
      delete document.documentElement.dataset.headerPanelPending;
      request.controller.abort();
    };
    const waitForHeader = (request, targets) => new Promise(resolve => {
      let frame;
      const finish = ready => {
        cancelAnimationFrame(frame);
        request.controller.signal.removeEventListener('abort', abort);
        resolve(ready);
      };
      const abort = () => finish(false);
      const check = () => {
        if (request.controller.signal.aborted || request.source?.isConnected === false || targets.some(header => !header.isConnected || !header.section?.isConnected)) return finish(false);
        targets.forEach(header => header.updatePlacement?.());
        if (targets.every(settled)) finish(true);
        else frame = requestAnimationFrame(check);
      };
      request.controller.signal.addEventListener('abort', abort, { once: true });
      frame = requestAnimationFrame(check);
    });
    const syncBackdrop = () => {
      const headerPanel = active && active.type !== 'assistant' ? active : null;
      document.querySelectorAll('[data-mega-close]').forEach(veil => {
        clearTimeout(veil.hideTimer);
        if (headerPanel) {
          window.OleaOverlayMotion.open(veil, () => { veil.hidden = false; veil.classList.add('is-visible'); });
        } else {
          window.OleaOverlayMotion.close(veil, () => { if (!active || active.type === 'assistant') { veil.classList.remove('is-visible'); veil.hidden = true; } });
        }
      });
      if (headerPanel) document.documentElement.dataset.headerPanel = headerPanel.type;
      else delete document.documentElement.dataset.headerPanel;
      document.querySelectorAll('smart-header').forEach(header => header.updateAppearance?.());
    };
    window.OleaHeaderPanels = {
      open(type, source, reveal) {
        cancel();
        const request = { type, source, intent: ++intent, controller: new AbortController() };
        pending = request;
        if (type === 'assistant') delete document.documentElement.dataset.headerPanelPending;
        else document.documentElement.dataset.headerPanelPending = type;
        const outgoing = active?.source;
        const containedSearch = outgoing?.search && source?.closest?.('dialog') === outgoing.search;
        const closed = outgoing && outgoing !== source && !containedSearch ? outgoing.closeForHeader?.() : null;
        const targets = type === 'assistant' ? [] : headers();
        targets.forEach(header => {
          header.section.classList.remove('is-header-hidden');
          header.anchorY = Math.max(0, window.scrollY);
          header.updateAppearance?.();
          header.updatePlacement?.();
        });
        const commit = async ready => {
          if (!ready || pending !== request || request.intent !== intent || request.controller.signal.aborted || source?.isConnected === false) {
            if (pending === request) cancel(source);
            return false;
          }
          pending = null;
          delete document.documentElement.dataset.headerPanelPending;
          targets.forEach(header => header.updatePlacement?.());
          const changed = active?.source !== source || active?.type !== type;
          active = { type, source };
          syncBackdrop();
          if (changed) document.dispatchEvent(new CustomEvent('olea:header-panel-open', { detail: { type, source } }));
          if (request.intent !== intent || active?.source !== source || source?.isConnected === false) {
            if (request.intent === intent) window.OleaHeaderPanels.release(source);
            return false;
          }
          try {
            if (await reveal?.() === false) {
              if (request.intent === intent && active?.source === source) window.OleaHeaderPanels.release(source);
              return false;
            }
            return true;
          } catch (error) {
            if (request.intent === intent && active?.source === source) window.OleaHeaderPanels.release(source);
            throw error;
          }
        };
        return Promise.resolve(closed).then(() => {
          if (pending !== request || request.controller.signal.aborted) return false;
          return targets.every(settled) ? commit(true) : waitForHeader(request, targets).then(commit);
        });
      },
      cancel,
      isPending(source) { return Boolean(pending && pending.source === source); },
      release(source) { cancel(source); if (active?.source === source) { active = null; if (!pending) intent += 1; syncBackdrop(); } },
      closeAll(restoreFocus = true) {
        cancel();
        intent += 1;
        active = null;
        syncBackdrop();
        document.dispatchEvent(new CustomEvent('olea:header-panel-close', { detail: { restoreFocus } }));
      }
    };
    document.addEventListener('keydown', event => {
      if ((active || pending) && event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); window.OleaHeaderPanels.closeAll(); }
    });
    document.addEventListener('click', event => {
      if (event.target.closest('[data-header-panel-close]')) window.OleaHeaderPanels.closeAll();
    });
    document.addEventListener('pointerdown', event => {
      if (pending && !pending.source?.contains?.(event.target) && !pending.source?.opener?.contains?.(event.target)) cancel();
    });
    window.addEventListener('pagehide', () => window.OleaHeaderPanels.closeAll(false));
  }
  if (!customElements.get('mega-menu')) {
    customElements.define('mega-menu', class extends HTMLElement {
      connectedCallback() {
        if (this.events) return;
        this.events = new AbortController();
        const signal = this.events.signal;
        this.mobileMedia = window.matchMedia('(max-width: 989px)');
        document.addEventListener('olea:header-panel-open', event => { if (event.detail.source !== this) this.close(); }, { signal });
        document.addEventListener('olea:header-panel-close', event => {
          const summary = this.details.find(detail => detail.open && !this.closing(detail))?.querySelector('summary');
          this.close();
          if (event.detail.restoreFocus) summary?.focus();
        }, { signal });
        this.details = [...this.querySelectorAll('details[data-mega]')];
        this.closePromises = new Map();
        this.activeDetail = null;
        this.clickedDetail = null;
        this.pointerDetail = null;
        this.intentVersion = 0;
        this.veil = this.querySelector('[data-mega-close]');
        this.details.forEach(detail => {
          detail.querySelector('summary')?.addEventListener('click', event => {
            if (!this.mobileMedia.matches && event.target.closest('[data-category-link]')) return;
            event.preventDefault();
            if (this.mobileMedia.matches) return;
            if ((detail.open && !this.closing(detail) || this.pendingDetail === detail) && (event.detail === 0 || this.clickedDetail === detail)) this.close();
            else { this.clickedDetail = detail; this.open(detail); }
          }, { signal });
          detail.addEventListener('pointerenter', event => {
            if (event.pointerType !== 'mouse' || this.mobileMedia.matches) return;
            if (document.documentElement.dataset.headerPanel === 'search' || document.documentElement.dataset.headerPanelPending === 'search') return;
            this.pointerDetail = detail;
            if (this.activeDetail !== detail) this.clickedDetail = null;
            clearTimeout(this.timer);
            this.open(detail);
          }, { signal });
          detail.addEventListener('pointerleave', event => {
            if (event.pointerType !== 'mouse' || this.mobileMedia.matches) return;
            if (this.pointerDetail === detail) this.pointerDetail = null;
            clearTimeout(this.timer);
            const version = this.intentVersion;
            this.timer = setTimeout(() => {
              if (version === this.intentVersion && (this.activeDetail === detail || this.pendingDetail === detail) && this.pointerDetail !== detail && !detail.contains(document.activeElement)) this.close();
            }, 220);
          }, { signal });
          detail.addEventListener('toggle', () => {
            if (detail.open && !this.closing(detail) && this.activeDetail !== detail && !this.pendingDetail) { detail.open = false; this.open(detail); }
            this.sync();
          }, { signal });
          detail.addEventListener('focusout', event => {
            if (!event.relatedTarget) return;
            const version = this.intentVersion;
            requestAnimationFrame(() => {
              if (version === this.intentVersion && this.activeDetail === detail && this.pointerDetail !== detail && !detail.contains(document.activeElement)) this.close();
            });
          }, { signal });
        });
        this.veil?.addEventListener('click', () => window.OleaHeaderPanels.closeAll(), { signal });
        this.addEventListener('click', event => {
          if (event.target.closest('[data-assistant-open]')) this.close();
          else if (event.target.closest('.mega-panel a[href],.header-nav-link[href],[data-category-link][href]')) this.close(true);
        }, { signal });
        this.addEventListener('keydown', event => {
          const summary = event.target.closest('summary');
          if (!summary) return;
          const detail = summary.closest('details[data-mega]');
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.open(detail, true);
          }
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            const navItems = [...this.querySelectorAll(':scope > details > summary,:scope > nav > ul > li > details > summary,:scope > nav > ul > li > a')];
            const index = navItems.indexOf(summary);
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? navItems.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + navItems.length) % navItems.length;
            this.close();
            navItems[next]?.focus();
          }
        }, { signal });
        document.addEventListener('keydown', event => {
          if (event.key !== 'Escape') return;
          const active = this.details.find(detail => detail.open);
          if (!active) return;
          event.preventDefault();
          this.close();
          active.querySelector('summary')?.focus();
        }, { signal });
        document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.close(); }, { signal });
        this.sync();
      }
      closing(detail) { return window.OleaOverlayMotion.isClosing(detail.querySelector('.mega-panel')); }
      closeForHeader() { return this.close(); }
      open(detail, focus = false) {
        if (!detail || this.mobileMedia?.matches) return;
        clearTimeout(this.timer);
        if (this.activeDetail === detail && detail.open && !this.closing(detail)) {
          if (focus) detail.querySelector('.mega-panel a')?.focus({ preventScroll: true });
          return;
        }
        const version = ++this.intentVersion;
        this.pendingDetail = detail;
        return window.OleaHeaderPanels.open('category', this, async () => {
          if (version !== this.intentVersion || !this.isConnected || this.mobileMedia.matches) return;
          this.activeDetail = null;
          await Promise.all(this.details.filter(other => other !== detail && other.open).map(other => this.closeDetail(other)));
          if (version !== this.intentVersion || !this.isConnected || this.mobileMedia.matches || this.pendingDetail !== detail) return false;
          this.activeDetail = detail;
          this.pendingDetail = null;
          detail.classList.remove('is-closing', 'is-switching', 'is-switching-out');
          const panel = detail.querySelector('.mega-panel');
          window.OleaOverlayMotion.open(panel, () => {
            panel.inert = false;
            panel.removeAttribute('aria-hidden');
            detail.open = true;
          });
          this.sync();
          if (focus) detail.querySelector('.mega-panel a')?.focus({ preventScroll: true });
        });
      }
      closeDetail(detail, immediate = false) {
        if (!detail.open) return Promise.resolve(true);
        const panel = detail.querySelector('.mega-panel');
        if (panel) { panel.inert = true; panel.setAttribute('aria-hidden', 'true'); }
        detail.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        const completed = window.OleaOverlayMotion.close(panel, () => {
          detail.open = false;
          detail.classList.remove('is-closing', 'is-switching', 'is-switching-out');
          this.closePromises.delete(detail);
          this.sync();
        }, { immediate });
        this.closePromises.set(detail, completed);
        return completed;
      }
      close(immediate = false) {
        clearTimeout(this.timer);
        this.activeDetail = null;
        this.clickedDetail = null;
        this.pendingDetail = null;
        this.pointerDetail = null;
        this.intentVersion += 1;
        const completed = Promise.all(this.details.map(detail => this.closeDetail(detail, immediate)));
        this.sync();
        return completed;
      }
      sync() {
        if (this.activeDetail && (!this.activeDetail.open || this.closing(this.activeDetail))) this.activeDetail = null;
        const open = Boolean(this.activeDetail);
        this.details.forEach(detail => detail.querySelector('summary')?.setAttribute('aria-expanded', String(detail.open && !this.closing(detail))));
        if (!open && !this.pendingDetail && !this.details.some(detail => detail.open)) window.OleaHeaderPanels.release(this);
        this.closest('smart-header')?.classList.toggle('has-menu-open', open);
      }
      disconnectedCallback() {
        this.close(true);
        window.OleaHeaderPanels.release(this);
        clearTimeout(this.timer);
        this.closePromises?.clear();
        this.events?.abort();
        this.events = null;
      }
    });
  }
  if (!customElements.get('smart-header')) {
    customElements.define('smart-header', class extends HTMLElement {
      connectedCallback() {
        if (this.events) return;
        this.events = new AbortController();
        const signal = this.events.signal;
        this.section = this.closest('.shopify-section');
        this.anchorY = window.scrollY;
        this.desktop = window.matchMedia('(min-width: 990px)');
        this.menu = this.querySelector('.mobile-menu');
        this.search = this.querySelector('.header-search-dialog');
        this.opener = null;
        this.silentDialogCloses = new WeakSet();
        this.dialogCloseTimers = new Map();
        this.dialogAfterClose = new Map();
        this.dialogOpeners = new Map();
        this.mobileNavControls = [...this.querySelectorAll('[data-mobile-menu-open],[data-mobile-family]')];
        this.mobileNavControls.forEach(control => { control.dataset.desktopControls = control.getAttribute('aria-controls') || ''; });
        this.syncNavigationControls();
        document.addEventListener('olea:header-panel-open', event => {
          if (event.detail.source === this || this.search && event.detail.source?.closest?.('dialog') === this.search) return;
          [this.menu, this.search].forEach(dialog => this.closeDialog(dialog, { restoreFocus: false }));
        }, { signal });
        document.addEventListener('olea:header-panel-close', event => { [this.menu, this.search].forEach(dialog => this.closeDialog(dialog, { restoreFocus: event.detail.restoreFocus })); }, { signal });
        this.updatePlacement();
        this.updateAppearance();
        document.addEventListener('olea:header-panel-open', () => this.updateAppearance(), { signal });
        document.addEventListener('olea:header-panel-close', () => this.updateAppearance(), { signal });
        window.addEventListener('scroll', () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            const y = Math.max(0, window.scrollY);
            const blocked = document.documentElement.dataset.headerPanel || document.documentElement.dataset.headerPanelPending || this.querySelector('dialog[open],details[open]') || this.contains(document.activeElement);
            if (y <= 100 || blocked) {
              this.section?.classList.remove('is-header-hidden');
              this.anchorY = y;
            } else if (Math.abs(y - this.anchorY) >= 8) {
              this.section?.classList.toggle('is-header-hidden', y > 240 && y > this.anchorY);
              this.anchorY = y;
            }
            this.updateAppearance();
            this.updatePlacement();
            this.ticking = false;
          });
        }, { passive: true, signal });
        window.addEventListener('resize', () => { this.updatePlacement(); this.updateAppearance(); }, { passive: true, signal });
        window.addEventListener('load', () => this.updateAppearance(), { once: true, signal });
        window.Olea.watchMedia(this.desktop, () => {
          window.OleaHeaderPanels.closeAll(false);
          this.querySelector('mega-menu')?.close();
          [this.menu, this.search].forEach(dialog => this.closeDialog(dialog, { immediate: true, restoreFocus: false }));
          this.section?.classList.remove('is-header-hidden');
          this.syncNavigationControls();
          this.updatePlacement();
        }, { signal });
        this.resizeObserver = new ResizeObserver(() => this.updatePlacement());
        this.resizeObserver.observe(this);
        this.section?.addEventListener?.('transitionend', event => {
          if (event.target === this.section && event.propertyName === 'transform') { this.updatePlacement(); this.updateAppearance(); }
        }, { signal });
        this.addEventListener('focusin', event => {
          this.section?.classList.remove('is-header-hidden');
          const rail = event.target.closest('.mega-panel__tiles,.header-nav>nav');
          if (!rail || rail.scrollWidth <= rail.clientWidth + 1) return;
          const item = event.target.closest('.mega-tile,li');
          if (!item || !rail.contains(item)) return;
          const bounds = rail.getBoundingClientRect(), rect = item.getBoundingClientRect();
          if (rect.left < bounds.left || rect.right > bounds.right) item.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches || !document.body.classList.contains('animations-enabled') ? 'auto' : 'smooth' });
        }, { signal });
        this.addEventListener('click', event => {
          const button = event.target.closest('button,[data-mobile-menu-open],[data-mobile-family]');
          if (!button) {
            const link = event.target.closest('.mobile-menu a[href]');
            if (link && this.menu?.open) {
              this.closeDialog(this.menu, { immediate: true, restoreFocus: false });
            }
            return;
          }
          if (button.matches('[data-dialog-close]') && button.closest('.mobile-menu,.header-search-dialog')) {
            event.preventDefault();
            event.stopPropagation();
            this.closeDialog(button.closest('dialog'));
            return;
          }
          if (!this.desktop.matches && button.matches('[data-mobile-menu-open],[data-mobile-family]')) {
            event.preventDefault();
            if (!this.menu?.open) this.resetMenu();
            this.openDialog(this.menu, button);
            return;
          }
          if (button.matches('[data-search-open]')) this.openDialog(this.search, button);
          if (button.matches('[data-mobile-next]')) this.goLevel(button.dataset.mobileNext);
          if (button.matches('[data-mobile-back]')) {
            const current = this.menu?.querySelector('[data-mobile-level]:not([hidden])');
            this.goLevel(current?.dataset.parentLevel || 'root');
          }
          if (button.matches('[data-assistant-open]') && this.menu?.open) {
            event.preventDefault();
            event.stopPropagation();
            this.closeDialog(this.menu, { restoreFocus: false, after: () => button.click() });
          }
        }, { signal });
        [this.menu, this.search].forEach(dialog => {
          if (!dialog) return;
          dialog.addEventListener('close', () => {
            if (dialog.open) return;
            clearTimeout(this.dialogCloseTimers.get(dialog));
            this.dialogCloseTimers.delete(dialog);
            dialog.classList.remove('is-closing');
            if (!this.menu?.open && !this.search?.open) window.OleaHeaderPanels.release(this);
            this.querySelectorAll(dialog === this.menu ? '[data-mobile-menu-open],[data-mobile-family]' : '[data-search-open]').forEach(button => button.setAttribute('aria-expanded', 'false'));
            if (dialog === this.menu) this.resetMenu();
            dialog.querySelectorAll('predictive-search').forEach(search => search.close?.());
            const opener = this.dialogOpeners.get(dialog);
            if (!this.silentDialogCloses.has(dialog) && opener?.isConnected) opener.focus({ preventScroll: true });
            this.silentDialogCloses.delete(dialog);
            this.dialogOpeners.delete(dialog);
            const after = this.dialogAfterClose.get(dialog);
            this.dialogAfterClose.delete(dialog);
            after?.();
          }, { signal });
          dialog.addEventListener('cancel', event => {
            event.preventDefault();
            this.closeDialog(dialog);
          }, { signal });
          dialog.addEventListener('click', event => {
            if (event.target !== dialog) return;
            const rect = dialog.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDialog(dialog);
          }, { signal });
        });
      }
      updateAppearance() {
        const hero = document.querySelector('body.template-index .home-hero,body.template-collection .collection-hero--image,.content-cover:not(.content-cover--plain),.content-contact--pro .content-contact__surface');
        const heroRect = hero?.getBoundingClientRect();
        const headerBoundary = Math.max(this.offsetHeight, this.getBoundingClientRect().bottom);
        const overHero = Boolean(heroRect && heroRect.top < headerBoundary && heroRect.bottom > headerBoundary);
        this.classList.toggle('is-over-hero', overHero);
      }
      syncNavigationControls() {
        this.mobileNavControls?.forEach(control => {
          const id = this.desktop.matches ? control.dataset.desktopControls : this.menu?.id;
          if (id) control.setAttribute('aria-controls', id);
          if (!this.desktop.matches) control.setAttribute('aria-haspopup', 'dialog');
          else control.removeAttribute('aria-haspopup');
        });
      }
      updatePlacement() {
        const rect = this.getBoundingClientRect();
        const announcement = document.querySelector('.announcement');
        const search = this.querySelector('.header-search-desktop .predictive-search__form')?.getBoundingClientRect();
        const placement = {
          '--announcement-height': Math.round(announcement?.getBoundingClientRect().height || 0),
          '--header-height': Math.round(rect.height),
          '--header-bottom': Math.max(0, Math.round(rect.bottom)),
          '--header-search-bottom': Math.max(0, Math.round(search?.width > 0 && search.height > 0 ? search.bottom : rect.bottom))
        };
        Object.entries(placement).forEach(([property, value]) => {
          if (this.placement?.[property] !== value) document.documentElement.style.setProperty(property, `${value}px`);
        });
        this.placement = placement;
      }
      closeForHeader() { return Promise.all([this.closeDialog(this.menu, { restoreFocus: false }), this.closeDialog(this.search, { restoreFocus: false })]); }
      openDialog(dialog, button) {
        if (!dialog) return;
        if (this.pendingDialog === dialog && window.OleaHeaderPanels.isPending(this)) { this.closeDialog(dialog); return; }
        if (window.OleaOverlayMotion.isClosing(dialog)) {
          clearTimeout(this.dialogCloseTimers.get(dialog));
          this.dialogCloseTimers.delete(dialog);
          this.dialogAfterClose.delete(dialog);
          dialog.classList.remove('is-closing');
          this.silentDialogCloses.delete(dialog);
        } else if (dialog.open) {
          this.closeDialog(dialog);
          return;
        }
        const other = dialog === this.menu ? this.search : this.menu;
        if (other?.open) {
          this.closeDialog(other, { restoreFocus: false, after: () => this.openDialog(dialog, button) });
          return;
        }
        this.querySelector('mega-menu')?.close();
        this.opener = button;
        this.pendingDialog = dialog;
        return window.OleaHeaderPanels.open(dialog === this.search ? 'search' : 'menu', this, () => {
          if (this.pendingDialog !== dialog || !this.isConnected) return;
          this.pendingDialog = null;
          this.dialogAfterClose.delete(dialog);
          this.silentDialogCloses.delete(dialog);
          this.dialogOpeners.set(dialog, button);
          window.OleaOverlayMotion.show(dialog);
          button?.setAttribute('aria-expanded', 'true');
          if (dialog === this.menu && button?.hasAttribute('data-mobile-family')) this.goLevel(button.dataset.mobileFamily, true, false);
          if (dialog === this.search) dialog.querySelector('input[type=search]')?.focus({ preventScroll: true });
        });
      }
      closeDialog(dialog, { immediate = false, restoreFocus = true, after } = {}) {
        if (dialog && this.pendingDialog === dialog) { this.pendingDialog = null; window.OleaHeaderPanels.cancel(this); }
        if (!dialog?.open) return window.OleaOverlayMotion.finished(dialog).then(() => { after?.(); return true; });
        if (after) this.dialogAfterClose.set(dialog, after);
        if (!restoreFocus) this.silentDialogCloses.add(dialog);
        this.querySelectorAll(dialog === this.menu ? '[data-mobile-menu-open],[data-mobile-family]' : '[data-search-open]').forEach(button => button.setAttribute('aria-expanded', 'false'));
        return window.OleaOverlayMotion.hide(dialog, { immediate });
      }
      goLevel(level, focus = true, animate = true) {
        if (!this.menu) return;
        const levels = [...this.menu.querySelectorAll('[data-mobile-level]')];
        const active = levels.find(node => node.dataset.mobileLevel === level);
        if (!active) return;
        levels.forEach(node => { node.hidden = node !== active; });
        this.menu.querySelector('[data-mobile-title]').textContent = active.dataset.title;
        const subtitle = this.menu.querySelector('[data-mobile-subtitle]');
        if (subtitle) subtitle.textContent = active.dataset.subtitle || '';
        const back = this.menu.querySelector('[data-mobile-back]');
        back.hidden = level === 'root';
        this.menu.querySelectorAll('[data-mobile-next]').forEach(button => button.setAttribute('aria-expanded', String(button.dataset.mobileNext === level)));
        const scrollArea = this.menu.querySelector('.mobile-menu__body');
        if (scrollArea) scrollArea.scrollTop = 0;
        const collectionScrollArea = active.querySelector('.mobile-collection-panel__body');
        if (collectionScrollArea) collectionScrollArea.scrollTop = 0;
        levels.forEach(node => node.classList.remove('is-entering'));
        if (focus && animate) { active.getBoundingClientRect(); active.classList.add('is-entering'); }
        if (focus) (level === 'root' ? active.querySelector('button,a') : back)?.focus();
      }
      resetMenu() { this.goLevel('root', false); }
      disconnectedCallback() {
        window.OleaHeaderPanels.release(this);
        this.dialogCloseTimers?.forEach(timer => clearTimeout(timer));
        this.events?.abort();
        this.events = null;
        this.resizeObserver?.disconnect();
      }
    });
  }
})();


